import React from 'react';
import { Condition, BulkUpdateFields } from '../types';
import { X, ArrowRight } from 'lucide-react';
import { StatusBadge } from './StatusBadge';
interface PreviewChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedConditions: Condition[];
  updates: BulkUpdateFields;
}
export function PreviewChangesModal({
  isOpen,
  onClose,
  onConfirm,
  selectedConditions,
  updates
}: PreviewChangesModalProps) {
  if (!isOpen) return null;
  const updateKeys = Object.keys(updates) as Array<keyof BulkUpdateFields>;
  if (updateKeys.length === 0) {
    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-191">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-id="element-192">
          <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-193">
            No Changes Selected
          </h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-194">
            Please select at least one field to update before previewing
            changes.
          </p>
          <div className="flex justify-end" data-id="element-195">
            <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" data-id="element-196">
              Close
            </button>
          </div>
        </div>
      </div>;
  }
  const renderValue = (key: keyof Condition, value: any) => {
    if (value === null || value === undefined) return <span className="text-gray-400 italic" data-id="element-197">Empty</span>;
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (key === 'status') return <StatusBadge status={value} data-id="element-198" />;
    if (key.toLowerCase().includes('date')) {
      return new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    return String(value);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-199">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" data-id="element-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-201">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-202">
            Preview Changes ({selectedConditions.length} conditions)
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-203">
            <X className="w-5 h-5" data-id="element-204" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1" data-id="element-205">
          <div className="space-y-6" data-id="element-206">
            {selectedConditions.map(condition => <div key={condition.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50" data-id="element-207">
                <div className="font-medium text-[#1C1C3A] mb-3" data-id="element-208">
                  {condition.name}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-209">
                  {updateKeys.map(key => {
                const oldValue = condition[key];
                const newValue = updates[key];
                // Skip if the value isn't actually changing
                if (oldValue === newValue) return null;
                return <div key={key} className="flex items-center text-sm bg-white p-2 rounded border border-gray-100 shadow-sm" data-id="element-210">
                        <span className="w-1/3 text-gray-500 font-medium capitalize truncate pr-2" data-id="element-211">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <div className="w-2/3 flex items-center justify-between" data-id="element-212">
                          <div className="flex-1 truncate pr-2" data-id="element-213">
                            {renderValue(key, oldValue)}
                          </div>
                          <ArrowRight className="w-4 h-4 text-[#D44444] flex-shrink-0 mx-2" data-id="element-214" />
                          <div className="flex-1 truncate font-medium text-[#D44444]" data-id="element-215">
                            {renderValue(key, newValue)}
                          </div>
                        </div>
                      </div>;
              })}
                </div>
              </div>)}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3 rounded-b-lg" data-id="element-216">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1C1C3A]" data-id="element-217">
            Cancel
          </button>
          <button onClick={() => {
          onConfirm();
          onClose();
        }} className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#D44444] hover:bg-[#B83333] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D44444]" data-id="element-218">
            Apply Changes
          </button>
        </div>
      </div>
    </div>;
}